import { useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import GlobalModal from '../components/global-modal/GlobalModal';
import { FETCH_LATEST_PROJECT_IS_DISABLED } from '../constants/features/constants';
import { PAGE_TITLE_SUFFIX } from '../constants';
import { getLatestProjectForUser } from '../project/project-actions';
import { fetchAuthenticatedUser } from '../user/user-actions';
import useFacebookPixel from '../tracking/useFacebookPixel';
import useGoogleTagManager from '../tracking/useGoogleTagManager';
import { Outlet, ScrollRestoration, useLocation, useParams } from 'react-router';
import { useRouteChangeDispatch } from '../components/hooks/useRouteChangeDispatch';
import { useHead, useTitleTemplate } from 'hoofd';
import UpdateContactInfo from '../user/update-contact-info/UpdateContactInfo';
import { DebugGrid } from '../debug-grid/DebugGrid';
import usePostHog from '../tracking/usePostHog';

import '../styles/all.less';
import AdministerCookieConsentModal from '../components/modal-cookie-consent/AdministerCookieConsentModal';
import Secret from './Secret';
import { useTrackPostHogPageViews } from '../tracking/useTrackPostHogPageViews';
import ModalCookieConsent from '../components/modal-cookie-consent/ModalCookieConsent';
import ScrollToHashElement from './ScrollToHashElement';
import { useSnapchatPixel } from '../tracking/useSnapchatPixel';
import { fetchFeatures } from '../features/features-action';
import { fetchCMSAllEntriesByContentType } from '../cms/cms-actions';
import { CMS_CONTENT_TYPE_CAMPAIGN } from '../campaign/campaign-card/CampaignConstants';

const Root = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const { user, shouldFetchLastProject, fetchLatestProjectEnabled } = useSelector(
        (state) => ({
            user: state.user,
            shouldFetchLastProject: state.projects.shouldFetchLastProject,
            fetchLatestProjectEnabled: !state.features[FETCH_LATEST_PROJECT_IS_DISABLED],
        }),
        shallowEqual
    );

    const userId = user?.id || user.anonymousData?.id;
    useGoogleTagManager();
    useFacebookPixel();
    useSnapchatPixel();
    usePostHog();
    useTrackPostHogPageViews();

    useRouteChangeDispatch();

    useTitleTemplate(`%s${PAGE_TITLE_SUFFIX}`);

    useHead({
        language: 'no',
        // The title field here must be non-empty for title hydration to work
        // why is beyond me
        title: '',
        metas: [
            {
                name: 'description',
                content:
                    'Med Spleis kan du lekende lett lage en digital innsamlingsaksjon og samle inn penger til å oppfylle både små og store drømmer.',
            },
            {
                name: 'keywords',
                content: 'crowdfunding, crowdfunding norge, folkefinansiering, pengeinnsamling, finansiering',
            },
            {
                property: 'fb:app_id',
                content: '461880350681193',
            },
            {
                property: 'og:image',
                content: 'https://static.spleis.no/images/spleis_og_poster.png',
            },
            { property: 'og:type', content: 'website' },
        ],
    });

    useEffect(() => {
        if (fetchLatestProjectEnabled && userId && shouldFetchLastProject) {
            dispatch(getLatestProjectForUser());
        }
    }, [userId, shouldFetchLastProject, dispatch, fetchLatestProjectEnabled]);

    useEffect(() => {
        dispatch(fetchAuthenticatedUser());
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchFeatures());
    }, [dispatch]);

    let locationSlug = location.pathname.replace(/\//g, '-');
    if (locationSlug === '-') {
        locationSlug = '-front-page';
    }

    return (
        <div className={`page${locationSlug}`}>
            <DebugGrid />
            <Secret />
            <ScrollRestoration
                getKey={(location) => (location.state?.useLocationKey ? location.key : location.pathname)}
            />
            <ScrollToHashElement />
            <UpdateContactInfo />
            <GlobalModal dispatch={dispatch} />
            <ModalCookieConsent />
            <AdministerCookieConsentModal />
            <Outlet />
        </div>
    );
};

export const loader = async ({ dispatch }) => {
    await dispatch(fetchCMSAllEntriesByContentType({ contentType: CMS_CONTENT_TYPE_CAMPAIGN }));
};

export default Root;
